import { type DetailedCardApiModel } from '@webapp/modules/spending-cards/data-access/api-models/DetailedCardApiModel'
import { type DetailedCardConstructor } from '../../entities/DetailedCard'

function isValidAddress(
  address:
    | DetailedCardApiModel['shipping_address']
    | DetailedCardApiModel['cardholder']['billing_address']
): boolean {
  return Boolean(
    address?.address_line_one &&
    address?.postal_code &&
    address?.city &&
    address?.country
  )
}

/**
 * Maps an address from API format to constructor format
 */
function mapAddress<
  T extends
    | DetailedCardApiModel['shipping_address']
    | DetailedCardApiModel['cardholder']['billing_address'],
  R extends
    | DetailedCardConstructor['shippingAddress']
    | DetailedCardConstructor['cardholder']['billingAddress'],
>(
  address: T,
  options: {
    addressType: 'shipping' | 'billing'
  }
): R {
  // Check if address is null or undefined
  if (!address) {
    throw new Error(`Missing address argument`)
  }
  // validate potential null values
  if (!isValidAddress(address)) {
    throw new Error(`Invalid ${options.addressType} address`)
  }

  const result = {
    name: address.name,
    addressLine1: address.address_line_one,
    addressLine2: address.address_line_two,
    postalCode: address.postal_code,
    city: address.city,
    stateProvince: address.state_province,
    country: address.country,
  }

  if (options.addressType === 'billing') {
    return {
      ...result,
      name: address.name,
    } as R
  } else {
    return {
      ...result,
      ...(address.name ? { name: address.name } : {}),
    } as R
  }
}

export function mapBillingAddress(
  billingAddress: DetailedCardApiModel['cardholder']['billing_address']
): DetailedCardConstructor['cardholder']['billingAddress'] {
  return mapAddress(billingAddress, {
    addressType: 'billing',
  })
}

/**
 * Maps a shipping address from API format to constructor format
 */
export function mapShippingAddress(
  shippingAddress: DetailedCardApiModel['shipping_address']
): DetailedCardConstructor['shippingAddress'] {
  return mapAddress(shippingAddress, {
    addressType: 'shipping',
  })
}

export const mapDetailedCardApiModel = (
  card: DetailedCardApiModel
): DetailedCardConstructor => ({
  id: card.id,
  externalId: card.external_id,
  last4Digits: card.number,
  type: card.category,
  status: card.status,
  cardholder: {
    id: card.cardholder.id,
    firstName: card.cardholder.first_name,
    lastName: card.cardholder.last_name,
    profileImage: card.cardholder.profile_image,
    email: card.cardholder.email,
    secondaryCategoryId: card.cardholder.department_id,
    tosAccepted: card.cardholder.tos_accepted,
    status: card.cardholder.status,
    billingAddress: mapBillingAddress(card.cardholder.billing_address),
    externalId: card.cardholder.external_id,
    userId: card.cardholder.user_id,
    provider: card.cardholder.provider,
  },
  createdAt: card.created_at,
  updatedAt: card.updated_at,
  requireActivation: card.require_activation,
  balance: card.balance,
  currencyName: card.currency,
  shippingAddress: card?.shipping_address
    ? mapShippingAddress(card.shipping_address)
    : null,
})
